import NiceModal from "@ebay/nice-modal-react";
import ModalBase from "modals/base/ModalBase";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { fas } from "@fortawesome/free-solid-svg-icons";
import biometricService from "shared/BiometricService";
import { toast } from "react-toastify";
import style from "./PinRegistrationModal.module.css";
import { devLog } from "utils/logger";
const PinRegistrationModal = NiceModal.create(({ 
    onRegistrationComplete,
    isForced = true,
    title = "PIN 번호 등록",
    message = "보안을 위해 PIN 번호 등록이 필요합니다."
}) => {
    const { t } = useTranslation();
    const modal = useRef();
    const [isRegistering, setIsRegistering] = useState(false);
    const [withHeader, withFooter] = [false, false];
    const [width, height] = ["90%", "auto"];
    const withCloseButton = !isForced;
    const clickOutsideToClose = !isForced;
    const buttonText = t("close");

    const handlePinRegistration = async () => {
        if (isRegistering) return;

        try {
            setIsRegistering(true);
            devLog("[PinRegistrationModal] PIN 등록 시작");

            const platform = biometricService.getPlatform();
            let result;

            if (platform === 'ios') {
                
                result = await biometricService.requestPinRegistration();
            } else {
                result = await biometricService.requestPinRegistration();
            }

            devLog("[PinRegistrationModal] PIN 등록 결과:", result);

            if (result.success) {
                toast.success("🎉 PIN 번호가 성공적으로 등록되었습니다!");
                
                if (typeof onRegistrationComplete === 'function') {
                    onRegistrationComplete(true, result);
                }
                
                if (modal.current) {
                    modal.current.close();
                }
            } else {
                throw new Error(result.errorMessage || 'PIN 등록에 실패했습니다.');
            }

        } catch (error) {
            console.error("[PinRegistrationModal] PIN 등록 오류:", error);
            
            if (error.message.includes('취소')) {
                toast.info("PIN 등록이 취소되었습니다.");
                
                if (isForced) {
                    toast.warning("⚠️ PIN 등록은 필수입니다. 다시 시도해주세요.");
                }
            } else {
                toast.error(error.message || "❌ PIN 등록 중 오류가 발생했습니다.");
            }

            if (typeof onRegistrationComplete === 'function') {
                onRegistrationComplete(false, error);
            }

            if (!isForced && modal.current) {
                modal.current.close();
            }
        } finally {
            setIsRegistering(false);
        }
    };

    const handleSkip = () => {
        if (!isForced) {
            devLog("[PinRegistrationModal] PIN 등록 건너뛰기");
            
            if (typeof onRegistrationComplete === 'function') {
                onRegistrationComplete(false, new Error('사용자가 등록을 건너뛰었습니다.'));
            }
            
            if (modal.current) {
                modal.current.close();
            }
        }
    };

    return (
        <ModalBase
            ref={modal}
            width={width}
            height={height}
            withHeader={withHeader}
            withFooter={withFooter}
            withCloseButton={withCloseButton}
            clickOutsideToClose={clickOutsideToClose}
            title=""
            buttonText={buttonText}
            borderRadius={"24px"}
        >
            <div className={style.container}>
                {/* 메인 아이콘과 배경 */}
                <div className={style.heroSection}>
                    <div className={style.iconBackground}>
                        <FontAwesomeIcon 
                            icon={fas.faLock} 
                            className={style.mainIcon}
                        />
                    </div>
                    <div className={style.sparkles}>
                        <div className={style.sparkle}></div>
                        <div className={style.sparkle}></div>
                        <div className={style.sparkle}></div>
                    </div>
                </div>

                {/* 타이틀 */}
                <div className={style.titleSection}>
                    <h2 className={style.title}>PIN 번호 등록하기 🔒</h2>
                    <p className={style.subtitle}>
                        안전한 결제를 위한 PIN 번호를 설정해주세요
                    </p>
                </div>

                {/* 기능 카드 - 심플하게 3개만 */}
                <div className={style.featureSection}>
                    <div className={style.featureCard}>
                        <FontAwesomeIcon icon={fas.faShield} className={style.featureIcon} />
                        <span className={style.featureName}>결제 보안</span>
                    </div>
                    <div className={style.featureCard}>
                        <FontAwesomeIcon icon={fas.faFingerprint} className={style.featureIcon} />
                        <span className={style.featureName}>이중 인증</span>
                    </div>
                    <div className={style.featureCard}>
                        <FontAwesomeIcon icon={fas.faUserLock} className={style.featureIcon} />
                        <span className={style.featureName}>개인정보 보호</span>
                    </div>
                </div>

                {/* 버튼 그룹 */}
                <div className={style.buttonSection}>
                    <button 
                        className={style.registerButton}
                        onClick={handlePinRegistration}
                        disabled={isRegistering}
                    >
                        {isRegistering ? (
                            <>
                                <FontAwesomeIcon icon={fas.faSpinner} spin className={style.buttonIcon} />
                                <span>등록 중...</span>
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={fas.faLock} className={style.buttonIcon} />
                                <span>PIN 번호 등록하기</span>
                            </>
                        )}
                    </button>
                    
                    {!isForced && (
                        <button 
                            className={style.skipButton}
                            onClick={handleSkip}
                            disabled={isRegistering}
                        >
                            나중에 등록하기
                        </button>
                    )}
                </div>
            </div>
        </ModalBase>
    );
});

export default PinRegistrationModal; 